import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Clock, CheckCircle, LogOut, History, Settings, Coffee, Wrench, Store, Truck, Phone, RefreshCw, PackageX, ShoppingBag, MapPin, Bell, Users, MessageSquare } from 'lucide-react';
import InstallPWA from '@/components/InstallPWA';
import axios from 'axios';
import { format } from 'date-fns';
import { APP_VERSION, LAST_UPDATED } from '@/utils/version';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TASKS = [
  'Admin/Maintain X',
  'Break / Lunch',
  'In-Store Repair',
  'In-Store Service',
  'On-site Repair',
  'On-site Service',
  'Phone Support',
  'Customer Service',
  'Refurb Repairs',
  'Returns / Warranties',
  'Store / Demo',
  'Travel',
  'Internal Meeting',
  'Cleaning/Organisation',
  'Taking a Shit',
  'Custom Task'
];

const TASK_COLORS = {
  'Admin/Maintain X': '#3B82F6',
  'Break / Lunch': '#10B981',
  'In-Store Repair': '#F59E0B',
  'In-Store Service': '#8B5CF6',
  'On-site Repair': '#EF4444',
  'On-site Service': '#EC4899',
  'Phone Support': '#06B6D4',
  'Customer Service': '#14B8A6',
  'Refurb Repairs': '#F97316',
  'Returns / Warranties': '#84CC16',
  'Store / Demo': '#6366F1',
  'Travel': '#64748B',
  'Internal Meeting': '#8B5CF6',
  'Cleaning/Organisation': '#22D3EE',
  'Taking a Shit': '#A78BFA',
  'Custom Task': '#F472B6'
};

const TASK_ICONS = {
  'Admin/Maintain X': Settings,
  'Break / Lunch': Coffee,
  'In-Store Repair': Wrench,
  'In-Store Service': Store,
  'On-site Repair': Truck,
  'On-site Service': Truck,
  'Phone Support': Phone,
  'Customer Service': Users,
  'Refurb Repairs': RefreshCw,
  'Returns / Warranties': PackageX,
  'Store / Demo': ShoppingBag,
  'Travel': MapPin,
  'Internal Meeting': Users,
  'Cleaning/Organisation': Wrench,
  'Taking a Shit': Coffee,
  'Custom Task': MessageSquare
};

const WORK_START_HOUR = 0;
const WORK_END_HOUR = 23;
const INTERVAL_MINUTES = 15;

const UserDashboard = () => {
  const { user, logout } = useAuth();
  const [currentTask, setCurrentTask] = useState(null);
  const [intervalStartTime, setIntervalStartTime] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(INTERVAL_MINUTES * 60);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [todayLogs, setTodayLogs] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [notificationPermission, setNotificationPermission] = useState('default');
  const audioRef = useRef(null);
  const [customTask, setCustomTask] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);

  // Request notification permission
  useEffect(() => {
    const requestNotificationPermission = async () => {
      if ('Notification' in window && 'serviceWorker' in navigator) {
        const permission = await Notification.requestPermission();
        setNotificationPermission(permission);
        
        if (permission === 'granted') {
          console.log('Notification permission granted');
        }
      }
    };

    requestNotificationPermission();

    // Create audio element with the custom MP3 - only once
    const audio = new Audio('https://customer-assets.emergentagent.com/job_brosight/artifacts/bllcc26u_Disturbed%20-%20The%20Sound%20Of%20Silence%20%28CYRIL%20Remix%29%20%5BOfficial%20Audio%5D.mp3');
    audio.loop = true;
    audio.volume = 0.7;
    audio.preload = 'auto'; // Preload the audio
    audioRef.current = audio;

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []); // Empty dependency array ensures this runs only once

  // Calculate next interval time
  const getNextIntervalTime = () => {
    const now = new Date();
    const currentMinutes = now.getHours() * 60 + now.getMinutes();
    const workStartMinutes = WORK_START_HOUR * 60;
    const workEndMinutes = WORK_END_HOUR * 60;

    if (currentMinutes < workStartMinutes || currentMinutes >= workEndMinutes) {
      return null;
    }

    const minutesSinceStart = currentMinutes - workStartMinutes;
    const intervalsCompleted = Math.floor(minutesSinceStart / INTERVAL_MINUTES);
    const nextIntervalMinutes = workStartMinutes + (intervalsCompleted * INTERVAL_MINUTES);

    const nextInterval = new Date();
    nextInterval.setHours(Math.floor(nextIntervalMinutes / 60));
    nextInterval.setMinutes(nextIntervalMinutes % 60);
    nextInterval.setSeconds(0);
    nextInterval.setMilliseconds(0);

    return nextInterval;
  };

  // Show notification
  const showNotification = useCallback(async () => {
    // Vibrate on mobile devices (works on Android)
    if ('vibrate' in navigator) {
      try {
        navigator.vibrate([200, 100, 200, 100, 200, 100, 200]);
      } catch (err) {
        console.log('Vibration not supported:', err);
      }
    }

    // Show system notification via Service Worker (appears in notification tray)
    if ('serviceWorker' in navigator && 'Notification' in window) {
      const permission = await Notification.requestPermission();
      
      if (permission === 'granted') {
        try {
          const registration = await navigator.serviceWorker.ready;
          
          // Show notification via Service Worker (shows in system tray!)
          await registration.showNotification('Bro Tracker - Time to Log', {
            body: 'What were you working on for the last minute, bro?',
            icon: '/logo.svg',
            badge: '/logo-small.svg',
            vibrate: [200, 100, 200, 100, 200],
            tag: 'bro-tracker-alert',
            requireInteraction: true,
            silent: false,
            data: {
              url: '/dashboard',
              timestamp: Date.now()
            },
            actions: [
              {
                action: 'log',
                title: 'Log Task'
              },
              {
                action: 'dismiss',
                title: 'Dismiss'
              }
            ]
          });
          
          console.log('Service Worker notification shown');
        } catch (err) {
          console.error('Service Worker notification failed:', err);
          
          // Fallback to regular notification
          try {
            new Notification('Bro Tracker - Time to Log', {
              body: 'What were you working on, bro?',
              icon: '/icon-192.png',
              badge: '/icon-192.png',
              vibrate: [200, 100, 200],
              tag: 'bro-tracker-alert',
              requireInteraction: true
            });
          } catch (e) {
            console.error('Regular notification also failed:', e);
          }
        }
      }
    }

    // Play the custom music instead of beeping
    if (audioRef.current) {
      try {
        // Don't reset currentTime if already playing - let it continue
        if (audioRef.current.paused) {
          audioRef.current.currentTime = 0; // Only reset if it was paused
        }
        audioRef.current.play().catch(err => {
          console.log('Audio play failed:', err);
        });
      } catch (err) {
        console.error('Audio error:', err);
      }
    }

    // Show task selection modal
    setShowTaskModal(true);
  }, []);

  // Stop music when modal closes
  useEffect(() => {
    if (!showTaskModal && audioRef.current) {
      try {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      } catch (err) {
        console.log('Audio stop error:', err);
      }
    }
  }, [showTaskModal]);

  // Timer logic
  useEffect(() => {
    if (!intervalStartTime) return;

    const interval = setInterval(() => {
      const now = new Date();
      const elapsed = Math.floor((now - intervalStartTime) / 1000);
      const remaining = (INTERVAL_MINUTES * 60) - elapsed;

      if (remaining <= 0) {
        setTimeRemaining(0);
        showNotification();
      } else {
        setTimeRemaining(remaining);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [intervalStartTime, showNotification]);

  // Check if within work hours and start timer
  useEffect(() => {
    const checkAndStartTimer = () => {
      const nextInterval = getNextIntervalTime();
      if (nextInterval && !intervalStartTime) {
        setIntervalStartTime(nextInterval);
      }
    };

    checkAndStartTimer();
    const interval = setInterval(checkAndStartTimer, 30000);

    return () => clearInterval(interval);
  }, [intervalStartTime]);

  // Fetch today's logs
  const fetchTodayLogs = async () => {
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      const response = await axios.get(`${API}/time-logs`, {
        params: {
          start_date: today.toISOString(),
          end_date: tomorrow.toISOString()
        }
      });
      setTodayLogs(response.data);
    } catch (error) {
      console.error('Failed to fetch logs:', error);
    }
  };

  useEffect(() => {
    fetchTodayLogs();
  }, []);

  const handleTaskSelect = async (task) => {
    try {
      // If custom task, check if user entered text
      if (task === 'Custom Task') {
        if (!customTask.trim()) {
          toast.error('Please enter a custom task name, bro!');
          return;
        }
        task = customTask.trim();
      }

      const endTime = new Date();
      const startTime = intervalStartTime || new Date(endTime.getTime() - INTERVAL_MINUTES * 60 * 1000);

      await axios.post(`${API}/time-logs`, {
        task,
        start_time: startTime.toISOString(),
        end_time: endTime.toISOString(),
        completed_early: timeRemaining > 0
      });

      setCurrentTask(task);
      setShowTaskModal(false);
      setCustomTask('');
      setShowCustomInput(false);
      toast.success('Task logged successfully, bro!');

      // Start next interval
      const nextInterval = getNextIntervalTime();
      if (nextInterval) {
        const newStart = new Date();
        setIntervalStartTime(newStart);
        setTimeRemaining(INTERVAL_MINUTES * 60);
      }

      fetchTodayLogs();
    } catch (error) {
      toast.error('Failed to log task');
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const isWorkHours = () => {
    const now = new Date();
    const hour = now.getHours();
    return hour >= WORK_START_HOUR && hour < WORK_END_HOUR;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50 backdrop-blur-lg bg-white/95">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex justify-between items-center h-14 sm:h-16">
            <div className="flex items-center gap-2 sm:gap-3">
              <img src="/logo-small.svg" alt="Bro Tracker" className="w-8 h-8 sm:w-10 sm:h-10" />
              <h1 className="text-xl sm:text-2xl font-bold" style={{ fontFamily: 'var(--font-heading)' }}>Bro Tracker</h1>
            </div>
            <div className="flex items-center gap-2 sm:gap-4">
              <span className="text-xs sm:text-sm text-gray-600 hidden sm:inline">Hello, {user?.username}</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => showNotification()}
                data-testid="test-notification-button"
                className="h-8 sm:h-10 px-2 sm:px-4"
              >
                <Bell className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Test Alert</span>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowHistory(!showHistory)}
                data-testid="toggle-history-button"
                className="h-8 sm:h-10 px-2 sm:px-4"
              >
                <History className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">History</span>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={logout}
                data-testid="logout-button"
                className="h-8 sm:h-10 px-2 sm:px-4"
              >
                <LogOut className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Logout</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-6 sm:py-12">
        {isWorkHours() ? (
          <div className="space-y-6 sm:space-y-8">
            {/* Timer Display */}
            <div className="bg-white border-2 border-primary rounded-2xl sm:rounded-xl shadow-lg p-6 sm:p-8 text-center" data-testid="timer-display">
              <div className="mb-3 sm:mb-4">
                <Clock className="w-12 h-12 sm:w-16 sm:h-16 text-primary mx-auto" />
              </div>
              <div className="text-5xl sm:text-7xl font-bold mb-2" style={{ fontFamily: 'var(--font-mono)', color: timeRemaining < 60 ? '#EF4444' : 'hsl(var(--primary))' }}>
                {formatTime(timeRemaining)}
              </div>
              <p className="text-base sm:text-lg text-gray-600">Time remaining in current interval</p>
            </div>

            {/* Current Task */}
            {currentTask && (
              <div className="bg-gray-100 border-l-4 p-6 rounded-lg" style={{ borderLeftColor: TASK_COLORS[currentTask] }}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Current Task</p>
                    <p className="text-2xl font-bold" style={{ fontFamily: 'var(--font-heading)' }}>{currentTask}</p>
                  </div>
                  <CheckCircle className="w-8 h-8 text-green-500" />
                </div>
              </div>
            )}

            {/* Complete Early Button */}
            <Button
              onClick={() => setShowTaskModal(true)}
              className="w-full h-12 sm:h-14 text-base sm:text-lg font-semibold rounded-xl"
              data-testid="complete-task-button"
            >
              Complete Task & Log Now, Bro
            </Button>

            {/* Notification Permission Banner */}
            {notificationPermission !== 'granted' && (
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-lg">
                <div className="flex items-start">
                  <Bell className="w-5 h-5 text-yellow-400 mt-0.5 mr-3 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-yellow-800">Enable Notifications</p>
                    <p className="text-sm text-yellow-700 mt-1">
                      Allow notifications to get alerts in your notification tray, bro!
                    </p>
                    <Button
                      onClick={async () => {
                        const permission = await Notification.requestPermission();
                        setNotificationPermission(permission);
                        if (permission === 'granted') {
                          toast.success('Notifications enabled! You\'ll see alerts in your notification tray.');
                        }
                      }}
                      variant="outline"
                      size="sm"
                      className="mt-2 border-yellow-400 text-yellow-800 hover:bg-yellow-100"
                    >
                      Enable Now
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Today's Activity */}
            {showHistory && todayLogs.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-lg p-6" data-testid="today-logs">
                <h2 className="text-xl font-bold mb-4" style={{ fontFamily: 'var(--font-heading)' }}>Today's Activity</h2>
                <div className="space-y-3">
                  {todayLogs.map((log) => (
                    <div key={log.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: TASK_COLORS[log.task] }}></div>
                        <div>
                          <p className="font-medium">{log.task}</p>
                          <p className="text-sm text-gray-600">
                            {format(new Date(log.start_time), 'HH:mm')} - {format(new Date(log.end_time), 'HH:mm')}
                          </p>
                        </div>
                      </div>
                      <span className="text-sm font-medium">{log.duration_minutes} min</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
            <Clock className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'var(--font-heading)' }}>Outside Work Hours</h2>
            <p className="text-gray-600">Time tracking is active from 9:00 AM to 5:00 PM</p>
          </div>
        )}
      </main>

      {/* Task Selection Modal */}
      <Dialog open={showTaskModal} onOpenChange={setShowTaskModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-3xl sm:rounded-2xl" data-testid="task-selection-modal">
          <DialogHeader>
            <DialogTitle className="text-xl sm:text-2xl" style={{ fontFamily: 'var(--font-heading)' }}>What were you working on, bro?</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4 mt-4">
            {TASKS.map((task) => {
              const IconComponent = TASK_ICONS[task];
              const isCustomTask = task === 'Custom Task';
              
              return (
                <button
                  key={task}
                  data-testid={`task-option-${task.toLowerCase().replace(/\s+/g, '-')}`}
                  onClick={() => {
                    if (isCustomTask) {
                      setShowCustomInput(true);
                    } else {
                      handleTaskSelect(task);
                    }
                  }}
                  className="p-3 sm:p-4 bg-white border-2 border-gray-200 hover:border-primary rounded-xl sm:rounded-lg transition-all hover:scale-105 active:scale-95 flex flex-col items-center"
                  style={{
                    transitionProperty: 'transform, border-color',
                    transitionDuration: '0.2s',
                    transitionTimingFunction: 'ease-out'
                  }}
                >
                  <div 
                    className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center mx-auto mb-2" 
                    style={{ backgroundColor: TASK_COLORS[task] }}
                  >
                    <IconComponent className="w-6 h-6 sm:w-7 sm:h-7 text-white" strokeWidth={2.5} />
                  </div>
                  <p className="text-xs sm:text-sm font-medium text-center">{task}</p>
                </button>
              );
            })}
          </div>

          {/* Custom Task Input */}
          {showCustomInput && (
            <div className="mt-4 p-4 bg-gray-50 rounded-xl border border-gray-200">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Enter your custom task:
              </label>
              <div className="flex gap-2">
                <Input
                  type="text"
                  value={customTask}
                  onChange={(e) => setCustomTask(e.target.value)}
                  placeholder="What were you working on?"
                  className="flex-1"
                  autoFocus
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && customTask.trim()) {
                      handleTaskSelect('Custom Task');
                    }
                  }}
                />
                <Button
                  onClick={() => handleTaskSelect('Custom Task')}
                  disabled={!customTask.trim()}
                  size="sm"
                >
                  Log It
                </Button>
                <Button
                  onClick={() => {
                    setShowCustomInput(false);
                    setCustomTask('');
                  }}
                  variant="ghost"
                  size="sm"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* PWA Install Prompt */}
      <InstallPWA />

      {/* Footer Version Info */}
      <div className="fixed bottom-2 left-2 text-xs text-gray-400 bg-white/80 px-2 py-1 rounded">
        v{APP_VERSION}
      </div>
    </div>
  );
};

export default UserDashboard;
